import React from 'react';
import { useSelector } from 'react-redux';

import { getL10nNumber, getLanguage } from '@travel/i18n';
import { Minus } from '@travel/icons/ui';
import { useDeviceType, MOBILE_SCREEN } from '@travel/traveler-core/hooks';
import { CircleScore } from '@travel/ui';
import { cx } from '@travel/utils';

import ButtonNoStyle from 'components/ButtonNoStyle';
import ReviewLink from 'pages/ProviderListPage/components/ReviewLink';
import { getReviewScoreSummary } from 'store/providerReview/selectors';

import { Translate } from 'core/translate';
import { getReviewLabelText, roundReviewScore } from 'utils/review';

import styles from './overview.module.scss';

type Props = {
  /** on what parent page this component lives */
  onPage: 'reviewList' | 'providerInfoUndated' | 'providerInfoDated';
  className?: string;
  /** handle click see more review in PC */
  onClickSeeMoreReview?: () => void;
};

function OverviewReviews(props: Props) {
  const { onClickSeeMoreReview = () => {} } = props;

  const language = useSelector(getLanguage);

  const { averageScore, total, averageRating } = useSelector(getReviewScoreSummary);
  const isNullScore = !total || averageScore === null;
  const displayScore = roundReviewScore(averageScore);

  const isMobile = useDeviceType() === MOBILE_SCREEN;

  const reviewButton = (
    <ButtonNoStyle
      onClick={onClickSeeMoreReview}
      className={styles.reviewTotalCount}
      data-testid="providerInformation-overviewReviews-reviewsButton"
    >
      <Translate
        id="Provider_Info.Reviews_with_colon"
        data={{
          number_of_reviews: getL10nNumber(language, total || 0, 0),
        }}
      />
    </ButtonNoStyle>
  );

  return !isNullScore ? (
    <div
      className={cx(styles.overviewReviewsWrapper, styles.overviewInfoContainer)}
      data-testid="providerInformation-overviewReviews-wrapper"
    >
      {isMobile ? (
        <>
          <ReviewLink
            textClassName={styles.reviewAverageRatingName}
            market=""
            totalReviews={0}
            reviewScore={displayScore}
            reviewLabel={averageRating?.name}
          />

          <Minus color="default" />
          {reviewButton}
        </>
      ) : (
        <>
          <div className={styles.pcReviewTextContainer}>
            <h3 className={styles.blockTitle}>
              <Translate id="Provider_Info.Provider_Details.Guest_Review" />
            </h3>
            {getReviewLabelText(averageRating?.name, styles.reviewLabel)}

            {reviewButton}
          </div>
          <CircleScore
            circleIconClassName={cx(styles.circleScore)}
            score={isNullScore ? null : displayScore}
            size={80}
            max={5}
            strokeWidth={4}
            hasAnimation={true}
            scoreClassName={styles.reviewScoreCircleText}
          />
        </>
      )}
    </div>
  ) : null;
}

export default OverviewReviews;
